"use client"

import { useEffect, useState, useRef } from "react"
import { Link } from "react-router-dom"
import {
  LogOutIcon,
  HistoryIcon,
  LightbulbIcon,
  FileBarChartIcon,
  ChevronRightIcon,
  CircleAlertIcon,
  BanIcon,
  XIcon,
  MenuIcon,
  CandlestickChartIcon,
  UserCog2Icon,
  GitForkIcon,
  ChevronLeftIcon,
  SettingsIcon,
  Wallet2Icon,
  HandCoins,
  FolderClock,
  ShieldCheck,
  RefreshCw,
} from "lucide-react"
import CryptoJS from "crypto-js"
import appConfig from "../../utils/appConfig";

import Logo from "../../assests/Logo.jpg"
import { cn } from "../../utils/utils"
import { auth } from "../../firebase"
import formatCurrency from "../../utils/formatCurrency"
import { fetchFunds } from "../../FunctionCall/fetchFunds"

// Add this style tag
const DashboardNav = ({
  openUserProfileModal,
  openStepGuideModal,
  userDetails,
  adviceTradeData,
  ignoreTradesData,
  isExpanded,
  toggleExpand,
}) => {
  // Add this at the beginning of your component
  useEffect(() => {
    // Add animation styles to document
    const styleEl = document.createElement("style")
    styleEl.textContent = `
      @keyframes slideInFromLeft {
        0% {
          transform: translateX(-20px);
          opacity: 0;
        }
        100% {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      .animate-slideInFromLeft {
        animation: slideInFromLeft 0.3s ease-out forwards;
      }
    `
    document.head.appendChild(styleEl)

    return () => {
      document.head.removeChild(styleEl)
    }
  }, [])

  // Rest of your component...

  const Icons = {
    home: <FileBarChartIcon className="mr-5 h-[18px] w-[18px]" />,
    home2: <FileBarChartIcon className="mr-0 ml-1 h-[24px] w-[24px]" />,
    advice: <LightbulbIcon className="mr-5 h-[18px] w-[18px]" />,
    advice2: <LightbulbIcon className="mr-0 ml-1 h-[24px] w-[24px]" />,
    history: <HistoryIcon className="mr-5 h-[18px] w-[18px]" />,
    history2: <HistoryIcon className="mr-0 ml-1 h-[24px] w-[24px]" />,
    sub: <SettingsIcon className="mr-5 h-[18px] w-[18px]" />,
    sub2: <SettingsIcon className="mr-0 ml-1 h-[24px] w-[24px]" />,
    ignore: <BanIcon className="mr-5 h-[18px] w-[18px]" />,
    ignore2: <BanIcon className="mr-0 ml-1 h-[24px] w-[24px]" />,
    userRound: <UserCog2Icon className="mr-5 h-[18px] w-[18px]" />,
    userRound2: <UserCog2Icon className="mr-0 ml-1 h-[24px] w-[24px]" />,
    order: <CandlestickChartIcon className="mr-5 h-[18px] w-[18px]" />,
    order2: <CandlestickChartIcon className="mr-0 ml-1 h-[24px] w-[24px]" />,
    strategy: <GitForkIcon className="mr-5 h-[18px] w-[18px] -rotate-90" />,
    strategy2: <GitForkIcon className="mr-0 ml-1 h-[24px] w-[24px] -rotate-90" />,
    pricing: <HandCoins className="mr-5 h-[18px] w-[18px] " />,
    pricing2: <HandCoins className="mr-0 ml-1 h-[24px] w-[24px] " />,
    previousOrders: <FolderClock className="mr-5 h-[16px] w-[16px] " />,
    previousOrders2: <FolderClock className="mr-5 h-[16px] w-[16px] " />,
  }

  const sideBarNavItems = [
    {
      title: "Live Portfolio",
      href: "/user",
      path: "/user",
      icon: "home",
      icon2: "home2",
      tooltip: "Portfolio",
    },
    {
      title: "Advice",
      href: "/stock-recommendation",
      path: "stock-recommendation",
      icon: "advice",
      icon2: "advice2",
      tooltip: "Advice",
    },
    {
      title: "Order Details",
      href: "/order-details",
      path: "order-details",
      icon: "order",
      icon2: "order2",
      tooltip: "Order Details",
    },
    {
      title: "Exited Trade History",
      href: "/trade-history",
      path: "trade-history",
      icon: "history",
      icon2: "history2",
      tooltip: "Trade History",
    },
    {
      title: "Broker Settings",
      href: "/subscriptions",
      path: "subscriptions",
      icon: "sub",
      icon2: "sub2",
      tooltip: "Broker Settings",
    },
    // {
    //   title: "Model Portfolio",
    //   href: "/model-portfolio",
    //   path: "model-portfolio",
    //   icon: "strategy",
    //   icon2: "strategy2",
    //   tooltip: "Model Portfolio",
    // },
    {
      title: "Plans / Portfolios",
      href: "/pricing",
      path: "pricing",
      icon: "pricing",
      icon2: "pricing2",
      tooltip: "Advisor",
    },

    {
      title: "My Invoices",
      href: "/previous-orders",
      path: "previous-orders",
      icon: "previousOrders",
      icon2: "previousOrders2",
      tooltip: "Previous Orders",
    },

    {
      title: "Contact RA",
      href: "/contact-advisor",
      path: "contact-advisor",
      icon: "userRound",
      icon2: "userRound2",
      tooltip: "Advisor",
    },
    // {
    //   title: "Ignored Trades",
    //   href: "/ignore-trades",
    //   path: "ignore-trades",
    //   icon: "ignore",
    //   icon2: "ignore2",
    //   tooltip: "Ignore Trades",
    // },
  ]

  const [showLogoutModel, setShowLogoutModel] = useState(false)
  const [activeItemIndex, setActiveItemIndex] = useState(-1)
  const whiteLabelText = process.env.REACT_APP_WHITE_LABEL_TEXT
  const path = window.location.pathname
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const prevExpandedRef = useRef(isExpanded)
  const isInitialMount = useRef(true)
  const prevPathRef = useRef(path)
  const [isLoadingFunds, setIsLoadingFunds] = useState(false)

  // Only animate when the sidebar state changes (expand/collapse)
  useEffect(() => {
    // Only animate when transitioning from collapsed to expanded
    if (!isInitialMount.current && !prevExpandedRef.current && isExpanded) {
      // Only animate when transitioning from collapsed to expanded
      setShouldAnimate(true)
    } else {
      // In all other cases (including path changes and initial load), don't animate
      setShouldAnimate(false)
    }

    // Update refs for next comparison
    prevExpandedRef.current = isExpanded
    prevPathRef.current = path

    // If this is the initial mount, mark it as complete
    if (isInitialMount.current) {
      isInitialMount.current = false
    }
  }, [isExpanded, path])

  // Add this to ensure animations reset properly after they complete
  useEffect(() => {
    if (shouldAnimate) {
      // Reset animation state after animation completes
      const timer = setTimeout(() => {
        setShouldAnimate(false)
      }, 1000) // Adjust timing based on your animation duration

      return () => clearTimeout(timer)
    }
  }, [shouldAnimate])

  const handleToggleExpand = () => {
    toggleExpand()
  }

  // Find active item index for highlighting
  useEffect(() => {
    const index = sideBarNavItems.findIndex((item) => path === item.href || path.startsWith(`/${item.path}`))
    setActiveItemIndex(index)
  }, [path])

  var initials
  var fullName = userDetails && userDetails.name
  initials =
    fullName &&
    fullName
      .split(" ")
      .map((name) => name[0])
      .join("")
      .toUpperCase()

  const circumference = 2 * Math.PI * 20 // Adjust radius as needed

  const profileCompletion =
    userDetails && typeof userDetails.profile_completion === "number" ? userDetails.profile_completion : 0

  const strokeDashoffset = circumference - (profileCompletion / 100) * circumference

  const clientCode = userDetails && userDetails && userDetails.clientCode
  const apiKey = userDetails && userDetails?.apiKey
  const broker = userDetails && userDetails?.user_broker
  const jwtToken = userDetails && userDetails?.jwtToken
  const sid = userDetails && userDetails?.sid
  const serverId = userDetails && userDetails?.serverId
  const secretKey = userDetails && userDetails.secretKey

  const checkValidApiAnSecret = (details) => {
    try {
      const bytesKey = CryptoJS.AES.decrypt(details, "ApiKeySecret")
      const Key = bytesKey.toString(CryptoJS.enc.Utf8) // Convert to UTF-8 string

      if (Key) {
        return Key
      } else {
        throw new Error("Decryption failed or invalid key.")
      }
    } catch (error) {
      console.error("Error during decryption:", error.message)
      return null
    }
  }

  const [funds, setFunds] = useState(null)

  const getAllFunds = async () => {
    setIsLoadingFunds(true)
    try {
      const fetchedFunds = await fetchFunds(broker, clientCode, apiKey, jwtToken, secretKey, sid, serverId)

      if (fetchedFunds) {
        setFunds(fetchedFunds)
      } else {
        console.error("Failed to fetch funds.")
      }
    } catch (error) {
      console.error("Error fetching funds:", error)
    } finally {
      setIsLoadingFunds(false)
    }
  }

  useEffect(() => {
    // Call the function when the component mounts or when relevant props change
    if (broker && (clientCode || jwtToken)) {
      getAllFunds()
    }
  }, [broker, clientCode, apiKey, jwtToken, secretKey])

  const [isWrapped, setIsWrapped] = useState(false)

  useEffect(() => {
    const checkWrapping = () => {
      const container = document.getElementById("cash-balance-container")
      const label = document.getElementById("cash-balance-label")
      const value = document.getElementById("cash-balance-value")

      if (container && label && value) {
        setIsWrapped(container.offsetHeight > label.offsetHeight)
      }
    }

    checkWrapping()
    window.addEventListener("resize", checkWrapping)
    return () => window.removeEventListener("resize", checkWrapping)
  }, [])

  return (
    <div
      className={`sticky top-0 hidden lg:flex h-screen ${
        isExpanded ? "w-[320px]" : "w-[70px]"
      } transition-all duration-300 ease-in-out`}
    >
      <aside className="max-h-screen w-full flex flex-col lg:flex bg-[#0A0F1D] border-r border-[#2056DF]/20 shadow-xl">
        {/* Sidebar Header */}
        <div
          className={`w-full flex flex-row items-center ${
            isExpanded ? "px-5 py-4" : "px-3 py-6"
          } transition-all duration-300 ease-in-out border-b border-[#2056DF]/10 bg-gradient-to-r from-[#0A0F1D] to-[#0A0F1D]`}
        >
          {isExpanded ? (
            <div className="w-full flex items-center justify-between">
              <div className="flex items-center space-x-3 group/logo">
                <div className="relative overflow-hidden rounded-md">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2056DF]/30 to-transparent opacity-0 group-hover/logo:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  <img
                    onClick={handleToggleExpand}
                    src={Logo || "/placeholder.svg"}
                    alt="Logo"
                    className={`w-[36px] h-[36px] rounded-md shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer relative z-10 ${shouldAnimate ? "animate-slideInFromLeft" : ""}`}
                    style={{
                      animationFillMode: "both",
                    }}
                  />
                </div>
                <div className="overflow-hidden">
                  <div
                    className={`text-lg font-semibold text-white leading-none transition-all duration-500 ease-in-out flex items-center ${shouldAnimate ? "animate-slideInFromLeft" : ""}`}
                    style={{
                      maxWidth: isExpanded ? "200px" : "0",
                      opacity: isExpanded ? 1 : 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      animationDelay: "0.1s",
                      animationFillMode: "both",
                    }}
                  >
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-white/80">
                      {whiteLabelText}
                    </span>
                  </div>
                </div>
              </div>
              <div
                onClick={handleToggleExpand}
                className={`flex items-center justify-center text-white cursor-pointer bg-[#0A0F1D] hover:bg-gradient-to-r hover:from-[#2056DF]/20 hover:to-transparent p-1.5 rounded-full transition-all duration-300 w-8 h-8 hover:shadow-inner ${shouldAnimate ? "animate-slideInFromLeft" : ""}`}
                style={{
                  animationDelay: "0.2s",
                  animationFillMode: "both",
                }}
              >
                <ChevronLeftIcon
                  strokeWidth={2.5}
                  className="h-5 w-5 text-white/90 group-hover:text-white transition-colors duration-300"
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center w-full space-y-4">
              <div
                onClick={handleToggleExpand}
                className="flex items-center justify-center text-white cursor-pointer hover:bg-[#2056DF]/20 p-1.5 rounded-full transition-colors w-8 h-8"
              >
                <MenuIcon className="h-5 w-5 text-white/90" />
              </div>
              <div className="relative overflow-hidden rounded-md group">
                <div className="absolute inset-0 bg-gradient-to-br from-[#2056DF]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-md"></div>
                <img
                  src={Logo || "/placeholder.svg"}
                  alt="Logo"
                  className="mx-auto rounded-md w-[36px] h-[36px] shadow-md group-hover:shadow-lg transition-all duration-300"
                />
              </div>
            </div>
          )}
        </div>

        {/* Navigation Links */}
        <nav
          className={`bg-[#0A0F1D] grid items-start max-h-[calc(100vh-180px)] h-auto pb-[110px] custom-scroll-sidebar ${
            isExpanded ? "gap-y-0 overflow-y-auto" : "gap-y-1 overflow-y-auto"
          }`}
        >
          {sideBarNavItems.map((item, index) => {
            const Icon = Icons[item.icon]
            const Icon2 = Icons[item.icon2]
            const isActive = path === item.href || path.startsWith(`/${item.path}`)
            const animationDelay = `${0.05 * (index + 1)}s`

            return (
              item.href && (
                <div
                  key={index}
                  className="transition-all duration-300 ease-in-out relative"
                  style={{
                    opacity: isExpanded ? 1 : 0.85,
                  }}
                >
                  {/* Active indicator with animation */}
                  {isActive && (
                    <div
                      className="absolute left-0 top-0 bottom-0 w-1 bg-[#2056DF] rounded-r-full"
                      style={{
                        animation: "pulseIndicator 2s infinite",
                      }}
                    ></div>
                  )}

                  <Link className="" key={index} to={item.disabled ? "/" : item.href}>
                    <div
                      className={cn(
                        "group relative text-sm hover:bg-gradient-to-r hover:from-[#2056DF]/10 hover:to-transparent hover:text-white text-[14px] font-medium leading-[22px] flex items-center transition-all duration-200",
                        isActive
                          ? "bg-gradient-to-r from-[#2056DF]/15 to-transparent text-white"
                          : "bg-transparent text-gray-300",
                        item.disabled && "cursor-not-allowed opacity-80",
                        isExpanded ? "px-[20px] py-[12px] pl-[24px]" : "pl-[20px] py-[14px]",
                        isExpanded && shouldAnimate && "animate-slideInFromLeft",
                      )}
                      style={{
                        animationDelay: isExpanded && shouldAnimate ? animationDelay : "0s",
                        animationFillMode: "both",
                      }}
                    >
                      <div
                        className={`${isActive ? "text-[#2056DF]" : "text-gray-400 group-hover:text-[#2056DF]"} transition-colors duration-200`}
                      >
                        {isExpanded ? Icon : Icon2}
                      </div>
                      <span
                        className={`transition-all duration-300 ease-in-out ${
                          isExpanded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 absolute"
                        }`}
                      >
                        {isExpanded ? item.title : ""}
                      </span>

                      {isExpanded && item.icon === "advice" && adviceTradeData && adviceTradeData.length >= 1 && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-[#2056DF] text-white animate-pulse">
                          {adviceTradeData && adviceTradeData.length}
                        </span>
                      )}

                      {isExpanded && item.icon === "ignore" && ignoreTradesData && ignoreTradesData.length >= 1 && (
                        <span className="ml-3 text-[#2056DF] font-medium">
                          ({ignoreTradesData && ignoreTradesData.length})
                        </span>
                      )}
                      {!isExpanded && (
                        <div className="absolute hidden group-hover:flex min-w-[120px] left-[60px] top-1/2 -translate-y-1/2 items-center justify-center px-3 py-1.5 rounded-md text-xs font-medium bg-gradient-to-r from-[#2056DF] to-[#1E40AF] text-white z-50 shadow-lg animate-fadeIn">
                          {item.tooltip}
                          <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2056DF] rotate-45"></div>
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
              )
            )
          })}

          <div
            onClick={() => setShowLogoutModel(true)}
            className={cn(
              "w-full group relative cursor-pointer text-sm bg-transparent hover:bg-gradient-to-r hover:from-[#2056DF]/10 hover:to-transparent hover:text-white text-[14px] font-medium leading-[22px] flex items-center transition-all duration-200",
              isExpanded ? "px-[20px] py-[12px] pl-[24px]" : "pl-[20px] py-[14px]",
              isExpanded && shouldAnimate && "animate-slideInFromLeft",
            )}
            style={{
              animationDelay: isExpanded && shouldAnimate ? `${0.05 * (sideBarNavItems.length + 1)}s` : "0s",
              animationFillMode: "both",
            }}
          >
            <LogOutIcon
              className={`${
                isExpanded ? "h-[18px] w-[18px] mr-5" : "h-[18px] w-[18px] mr-0 ml-1"
              } text-gray-400 group-hover:text-[#2056DF] transition-colors duration-200`}
            />
            <span className={`${isExpanded ? "block" : "hidden"} text-gray-300 group-hover:text-white`}>Logout</span>
            {!isExpanded && (
              <div className="absolute hidden group-hover:flex min-w-[120px] left-[60px] top-1/2 -translate-y-1/2 items-center justify-center px-3 py-1.5 rounded-md text-xs font-medium bg-gradient-to-r from-[#2056DF] to-[#1E40AF] text-white z-50 shadow-lg animate-fadeIn">
                Logout
                <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2056DF] rotate-45"></div>
              </div>
            )}
          </div>
        </nav>

        {/* Profile Section - Incomplete Profile */}
        {isExpanded && userDetails && userDetails.profile_completion !== 100 && (
          <div className="absolute bottom-[46px] flex flex-col space-y-[10px] border-t border-[#2056DF]/10 w-[98%] px-2 py-[10px] bg-[#0A0F1D]">
            {broker && broker !== "" && (
              <div
                id="cash-balance-container"
                className={`flex flex-col sm:flex-row items-start bg-gradient-to-r from-[#111827] to-[#0A0F1D] py-2.5 px-4 rounded-md shadow-inner border border-[#2056DF]/10 ${shouldAnimate ? "animate-slideInFromLeft" : ""}`}
                style={{
                  opacity: isExpanded ? 1 : 0,
                  maxHeight: isExpanded ? "100px" : "0",
                  overflow: "hidden",
                  animationDelay: `${0.05 * (sideBarNavItems.length + 2)}s`,
                  animationFillMode: "both",
                }}
              >
                <div
                  id="cash-balance-label"
                  className="flex items-center text-[13px] font-medium text-white w-full sm:w-auto sm:mr-4 mb-1 sm:mb-0"
                >
                  <div className="flex flex-row items-center text-[13px] font-medium text-white">
                    <Wallet2Icon className="text-[#2056DF] h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="whitespace-nowrap">Cash Balance</span>
                  </div>
                </div>

                <div className="flex justify-between w-full sm:justify-end">
                  <div className="flex items-start">
                    <span id="cash-balance-value" className="flex items-center text-[14px] font-medium">
                      {isLoadingFunds ? (
                        <span className="text-white flex items-center">
                          <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                        </span>
                      ) : funds?.data?.availablecash && !isNaN(funds?.data?.availablecash) ? (
                        <span
                          className={
                            funds?.data?.availablecash > 0
                              ? "text-green-400"
                              : funds?.data?.availablecash < 0
                                ? "text-red-400"
                                : "text-white"
                          }
                        >
                          ₹{formatCurrency(Number.parseFloat(funds?.data?.availablecash).toFixed(2))}
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <Link to="/user">
                            <RefreshCw
                              className="h-4 w-4 mr-1 text-blue-400 hover:text-blue-300 hover:animate-spin cursor-pointer"
                              onClick={() => {
                                getAllFunds()
                              }}
                            />
                          </Link>
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="flex flex-row items-center min-w-max text-[12px] font-medium text-white ml-3">
                    <span className="text-gray-400 text-[11px] font-medium">({broker})</span>
                  </div>
                </div>
              </div>
            )}

            {/* Connect broker message */}
            {(!broker || broker === "") && (
              <div className="text-xs text-gray-400 px-1 mt-1">
                <Link to="/subscriptions" className="text-blue-400 hover:text-blue-300 flex items-center">
                  <span>Connect broker to display balance</span>
                  <ChevronRightIcon className="h-3 w-3 ml-1" />
                </Link>
              </div>
            )}

            <div
              className={`flex justify-between items-center w-full hover:cursor-pointer ${shouldAnimate ? "animate-slideInFromLeft" : ""}`}
              style={{
                animationDelay: `${0.05 * (sideBarNavItems.length + 3)}s`,
                animationFillMode: "both",
              }}
            >
              <div className="flex space-x-3 items-center text-white text-center">
                <div
                  onClick={openUserProfileModal}
                  className="relative flex items-center justify-center rounded-full cursor-pointer hover:scale-105 transition-transform duration-300"
                >
                  <svg className="transform -rotate-90 w-[50px] h-[50px]">
                    <circle
                      cx="25"
                      cy="25"
                      r="20"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="transparent"
                      className="text-[#1E3A8A]/30"
                    />
                    <circle
                      cx="25"
                      cy="25"
                      r="20"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="transparent"
                      className="text-[#2056DF]"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      strokeLinecap="round"
                      style={{
                        filter: "drop-shadow(0 0 2px rgba(32, 86, 223, 0.5))",
                      }}
                    />
                  </svg>
                  <div className="absolute flex items-center justify-center rounded-full">
                    {userDetails &&
                    userDetails?.image_url &&
                    userDetails?.image_url !== "" &&
                    userDetails?.image_url !== null ? (
                      <div className="flex flex-row justify-center w-full">
                        <img
                          src={userDetails?.image_url || "/placeholder.svg"}
                          alt="Logo"
                          className="rounded-full w-[35px] h-[35px] object-cover"
                        />
                      </div>
                    ) : (
                      <div className="flex font-medium text-[16px] items-center justify-center rounded-full text-white">
                        {initials || userDetails?.email.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-start max-w-[180px] overflow-hidden">
                  <span className="text-sm text-white font-medium truncate w-full text-left">
                    {userDetails && userDetails.name ? userDetails.name : userDetails?.email.split("@")[0]}
                  </span>
                  <span className="text-xs text-gray-300 font-normal truncate w-full">
                    {userDetails && userDetails.email}
                  </span>
                </div>
              </div>
            </div>

            <div
              className={`mt-1 flex flex-col space-y-3 w-full px-2 ${shouldAnimate ? "animate-slideInFromLeft" : ""}`}
              style={{
                animationDelay: `${0.05 * (sideBarNavItems.length + 4)}s`,
                animationFillMode: "both",
              }}
            >
              <button
                onClick={openUserProfileModal}
                className={`w-full flex items-center justify-center py-2 px-4 rounded-md text-white font-medium text-[13px] ${appConfig.colors.button}  hover:from-[#1E40AF] hover:to-[#2056DF] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5`}
              >
                Complete Profile
                <ChevronRightIcon className="h-4 w-4 text-white ml-1" />
              </button>
            </div>
          </div>
        )}

        {/* Profile Section - Collapsed Sidebar */}
        {!isExpanded && userDetails && userDetails.profile_completion !== 100 && (
          <div className="absolute bottom-2 flex items-center pl-2 text-white text-center mb-2">
            <div
              onClick={openUserProfileModal}
              className="relative flex items-center justify-center rounded-full cursor-pointer hover:scale-110 transition-transform duration-300"
            >
              <svg className="transform -rotate-90 w-[50px] h-[50px]">
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="transparent"
                  className="text-[#1E3A8A]/30"
                />
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="transparent"
                  className="text-[#2056DF]"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  style={{
                    filter: "drop-shadow(0 0 2px rgba(32, 86, 223, 0.5))",
                  }}
                />
              </svg>
              <div className="absolute flex items-center justify-center rounded-full">
                {userDetails &&
                userDetails?.image_url &&
                userDetails?.image_url !== "" &&
                userDetails?.image_url !== null ? (
                  <div className="flex flex-row justify-center w-full">
                    <img
                      src={userDetails?.image_url || "/placeholder.svg"}
                      alt="Logo"
                      className="rounded-full w-[35px] h-[35px] object-cover"
                    />
                  </div>
                ) : (
                  <div className="flex font-medium text-[16px] items-center justify-center rounded-full text-white">
                    {initials || userDetails?.email.slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Profile Section - Complete Profile */}
        {isExpanded && userDetails && userDetails.profile_completion === 100 && (
          <div className="absolute bottom-[46px] flex flex-col space-y-3 w-full border-t border-[#2056DF]/10 py-3 px-4 bg-[#0A0F1D]">
            {broker && broker !== "" && (
              <div
                className={`flex items-center justify-between bg-gradient-to-r from-[#111827] to-[#0A0F1D] py-2.5 rounded-md space-x-4 px-4 shadow-inner border border-[#2056DF]/10 ${shouldAnimate ? "animate-slideInFromLeft" : ""}`}
              >
                <div className="flex flex-row items-center min-w-max text-[13px] font-medium text-white">
                  <Wallet2Icon className="text-[#2056DF] h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="whitespace-nowrap">Cash Balance</span>
                </div>
                <span className="flex items-center text-[14px] font-medium">
                  {isLoadingFunds ? (
                    <span className="text-white flex items-center">
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    </span>
                  ) : funds?.data?.availablecash && !isNaN(funds?.data?.availablecash) ? (
                    <span
                      className={
                        funds?.data?.availablecash > 0
                          ? "text-green-400"
                          : funds?.data?.availablecash < 0
                            ? "text-red-400"
                            : "text-white"
                      }
                    >
                      ₹{formatCurrency(Number.parseFloat(funds?.data?.availablecash).toFixed(2))}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <RefreshCw
                        className="h-4 w-4 mr-1 text-blue-400 hover:text-blue-300 hover:animate-spin cursor-pointer"
                        onClick={() => {
                          getAllFunds()
                        }}
                      />
                      <Link to="/user" className="text-blue-400 hover:text-blue-300">
                        Reload
                      </Link>
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* Connect broker message */}
            {(!broker || broker === "") && (
              <div className="text-xs text-gray-400 px-1 mt-1">
                <Link to="/subscriptions" className="text-blue-400 hover:text-blue-300 flex items-center">
                  <span>Connect broker to display balance</span>
                  <ChevronRightIcon className="h-3 w-3 ml-1" />
                </Link>
              </div>
            )}

            <div
              className={`flex justify-between items-center w-full hover:cursor-pointer ${shouldAnimate ? "animate-slideInFromLeft" : ""}`}
            >
              <div className="flex space-x-3 items-center text-white text-center">
                {userDetails &&
                userDetails?.image_url &&
                userDetails?.image_url !== "" &&
                userDetails?.image_url !== null ? (
                  <div
                    className="flex items-center justify-center rounded-full ring-2 ring-[#2056DF] w-[40px] h-[40px] cursor-pointer overflow-hidden flex-shrink-0 hover:ring-[#1E40AF] transition-colors duration-200 hover:scale-105 transform"
                    onClick={openUserProfileModal}
                  >
                    <img
                      src={userDetails?.image_url || "/placeholder.svg"}
                      alt="Profile Image"
                      className="rounded-full w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div
                    className="flex font-medium text-[16px] items-center justify-center rounded-full border-2 border-[#2056DF] w-[40px] h-[40px] cursor-pointer flex-shrink-0 hover:border-[#1E40AF] transition-colors duration-200 hover:scale-105 transform"
                    onClick={openUserProfileModal}
                  >
                    {initials || userDetails?.email.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="flex flex-col items-start max-w-[180px] overflow-hidden">
                  <div className="flex items-center">
                    <span className="text-[14px] text-white font-medium truncate">
                      {userDetails && userDetails.name ? userDetails.name : userDetails?.email.split("@")[0]}
                    </span>
                    <ShieldCheck className="h-3.5 w-3.5 ml-1 text-[#2056DF] flex-shrink-0" />
                  </div>
                  <span className="text-[12px] text-gray-300 font-normal truncate w-full">
                    {userDetails && userDetails.email}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Profile Section - Complete Profile Collapsed */}
        {!isExpanded && userDetails && userDetails.profile_completion === 100 && (
          <div className="w-full absolute bottom-2 flex items-center justify-center text-white text-center mb-2">
            {userDetails &&
            userDetails?.image_url &&
            userDetails?.image_url !== "" &&
            userDetails?.image_url !== null ? (
              <div
                onClick={openUserProfileModal}
                className="cursor-pointer flex items-center justify-center rounded-full ring-2 ring-[#2056DF] w-[40px] h-[40px] overflow-hidden hover:ring-[#1E40AF] transition-colors duration-200 hover:scale-110 transform"
              >
                <img
                  src={userDetails?.image_url || "/placeholder.svg"}
                  alt="Profile Image"
                  className="rounded-full w-full h-full object-cover"
                />
              </div>
            ) : (
              <div
                onClick={openUserProfileModal}
                className="cursor-pointer flex font-medium text-[16px] items-center justify-center rounded-full border-2 border-[#2056DF] w-[40px] h-[40px] hover:border-[#1E40AF] transition-colors duration-200 hover:scale-110 transform"
              >
                {initials || userDetails?.email.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
        )}

        {/* Help Section */}
        <div
          className={`
    absolute 
    ${userDetails && userDetails.profile_completion === 100 ? "bottom-[8px]" : "bottom-2"}
    w-full bg-[#0A0F1D] border-t border-[#2056DF]/10 
    overflow-hidden transition-all duration-300 ease-in-out
    ${isExpanded && shouldAnimate ? "animate-slideInFromLeft" : ""}
  `}
          style={{
            maxHeight: isExpanded ? "40px" : "0",
            opacity: isExpanded ? 1 : 0,
            padding: isExpanded ? "8px 16px" : "0 16px",
            animationDelay: `${0.05 * (sideBarNavItems.length + 5)}s`,
            animationFillMode: "both",
          }}
        >
          <div
            className={`flex items-center text-gray-300 text-[11px] leading-[22px] font-normal
        transition-all duration-300 ease-in-out
    `}
            style={{
              transform: `translateY(${isExpanded ? "0" : "10px"})`,
              opacity: isExpanded ? 1 : 0,
            }}
          >
            <CircleAlertIcon className="text-[#2056DF] h-4 mr-1" />
            How to use {whiteLabelText}
            <Link
              onClick={openStepGuideModal}
              className="ml-2 text-[#2056DF] text-[11px] font-medium hover:text-[#1E40AF] transition-colors duration-200 hover:underline"
            >
              Click here
            </Link>
          </div>
        </div>

        {/* Logout Modal */}
        {showLogoutModel && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50">
            <div className="relative bg-[#111827] border border-[#2056DF]/20 rounded-lg shadow-xl max-w-md w-full mx-4 animate-modalFadeIn">
              <div
                className="absolute right-4 top-4 text-gray-400 hover:text-white cursor-pointer hover:scale-110 transition-transform duration-200"
                onClick={() => setShowLogoutModel(false)}
              >
                <XIcon className="w-5 h-5" />
              </div>
              <div className="p-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#0A0F1D] flex items-center justify-center border border-[#2056DF]/20">
                  <LogOutIcon className="h-8 w-8 text-[#FF0000]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4 text-center">Are you sure you want to logout?</h3>
                <div className="flex space-x-4 mt-6">
                  <button
                    onClick={() => setShowLogoutModel(false)}
                    className="flex-1 py-2.5 px-4 bg-[#1E293B] hover:bg-[#1E293B]/80 text-white font-medium rounded-md transition-all duration-200 hover:shadow-md"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => auth.signOut()}
                    className="flex-1 py-2.5 px-4 bg-gradient-to-r from-[#FF0000] to-[#DC2626] hover:from-[#DC2626] hover:to-[#B91C1C] text-white font-medium rounded-md transition-all duration-300 hover:shadow-md transform hover:-translate-y-0.5"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  )
}

export default DashboardNav
